import { ref } from 'vue';
import { defineStore } from 'pinia';
import { createResourceStore } from '@/stores/resource';
import { http } from '@/services/http';

export const useGroupsStore = createResourceStore<Record<string, unknown>>('skuGroups', '/api/sku-groups');
export const useActivitiesStore = createResourceStore<Record<string, unknown>>('activities', '/api/activities');
export const useRatiosStore = createResourceStore<Record<string, unknown>>('ratios', '/api/ratios/analysis');
export const useUsersStore = createResourceStore<Record<string, unknown>>('users', '/api/auth/users');

/** 审计变更项（后端 diff_json.changes 的形状） */
export interface AuditChange {
  field: string;
  before: string;
  after: string;
}
export interface AuditRow {
  id: number;
  created_at?: string;
  username?: string;
  action?: string;
  resource?: string;
  resource_id?: string | null;
  /** 1.1.68: 登录审计需要（audit_logs 表本来就有这几列） */
  ip?: string | null;
  user_agent?: string | null;
  status?: number | null;
  method?: string | null;
  path?: string | null;
  detail?: string | null;
  diff_json?: string | null;
}

/**
 * 操作审计（业务视图）—— 1.1.52
 * 只取「业务改动」：后端 scope=business 会滤掉 管理/系统类板块、登录动作与失败请求，
 * 并把历史英文板块名归一化成中文；counts 是该板块的改动条数（用于顶部分板块筛选）。
 */
export const useAuditStore = defineStore('auditBusiness', () => {
  const rows = ref<AuditRow[]>([]);
  const counts = ref<Record<string, number>>({});
  const loading = ref(false);
  const error = ref('');
  const loaded = ref(false);

  async function load(): Promise<void> {
    loading.value = true;
    error.value = '';
    try {
      const data = await http.get<{ rows: AuditRow[]; counts?: Record<string, number> }>(
        '/api/audit?scope=business&limit=500',
      );
      rows.value = data.rows || [];
      counts.value = data.counts || {};
      loaded.value = true;
    } catch (cause) {
      error.value = cause instanceof Error ? cause.message : '加载失败';
    } finally {
      loading.value = false;
    }
  }

  async function ensureLoaded(): Promise<void> {
    if (!loaded.value && !loading.value) await load();
  }

  return { rows, counts, loading, error, load, ensureLoaded };
});
